import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import DropDown from './common/DropDown/DropDown'
import { APP_ID_DSS_ID } from './common/GlobalConstants'
import { hooks } from '@sparky/framework'
import KeyValueDisplay from './common/KeyValueDisplay'
import { FF_ENABLE_CIE_SHARING } from '../../common/utils/FeatureFlags'
import { getVarValue, loadTsgInstances } from '../../common/utils/Sparky'
import { SUPER_ROOT_ID } from '../constants'
import { isNonemptyArray } from './common/utils'
import { updateActivationInstances } from '../actions'

import type { VFC } from 'react'
import type { AppManifest } from '@sparky/framework'
import type { CommonStoreState } from '../store'
import type { IDropDownData, IDropDownProps } from './common/DropDown/DropDown'
import type { BannerProps } from './unified/UAccordion'


const fieldName = 'cie'
const TITLE = 'Cloud Identity Engine'

type DefaultCIEFieldProps = {
  state: CommonStoreState,
  cieDropDownProps: IDropDownProps
  updateTitle?: (title: string) => void
  setDisable?: (disable: boolean) => void
  setBanner?: (props: BannerProps) => void
}
const { useGlobalEventListener } = hooks

const DefaultCIEField: VFC<DefaultCIEFieldProps> = (props) => {
  const { updateTitle, setDisable, state, setBanner } = props
  const { apps, instances, loadingTsg, tsgs, tsg, selectedPlatformRegion, supportAccountId, platformRegions } = state
  const { setValue, getValues, resetField, setError, clearErrors, trigger } = useFormContext()
  const [isCieDropdownDisabled, setCieDropdownDisable] = useState<boolean>(false)
  const [cieTitle, setCIETitle] = useState<string>('')
  const enableCieSharing = getVarValue(FF_ENABLE_CIE_SHARING, false)
  const [cieList, setCieList] = useState<IDropDownData[]>([])
  // TODO: update name and add handler
  useGlobalEventListener('sase-activation-events-[name]', () => {})

  const cieManifest = useMemo<AppManifest | undefined>(() => {
    return apps?.find(each => each.app_id === APP_ID_DSS_ID)
  }, [apps])

  const cieInstance = useMemo(() => {
    return instances.find(each => each.app_id === APP_ID_DSS_ID)
  }, [instances])

  const getParentCIEInstance: any = useCallback(async (_tsg: any, response: any) => {
    if (!_tsg || _tsg?.parent_id === SUPER_ROOT_ID) {
      return {}
    }
    const tsgInstance = await loadTsgInstances(_tsg?.parent_id)
    let dssInstance: any
    if (supportAccountId) {
      dssInstance = isNonemptyArray(tsgInstance) && tsgInstance.find((_each: any) =>
        _each.app_id === APP_ID_DSS_ID && (supportAccountId ? (+_each.support_account_id === +supportAccountId) : true))
    }
    if (selectedPlatformRegion && dssInstance?.region) {
      dssInstance = dssInstance.region === selectedPlatformRegion ? dssInstance : undefined
    }
    if (dssInstance && !dssInstance?.original_tsg_id) {
      response.push({
        value: `${dssInstance.tenant_instance_name}`,
        description: 'existing instance',
        key: dssInstance.tenant_id,
        id: dssInstance.tenant_id,
        parent_tsg: dssInstance.tsg_id
      })
    }
    return getParentCIEInstance(tsgs?.get(_tsg.parent_id), response)
  }, [selectedPlatformRegion, supportAccountId, tsgs])


  const getCieList = useCallback(async () => {
    const dropDownOption = [{
      value: 'Create New',
      description: 'Create New',
      key: 'new',
      id: 'new',
    },
    {
      value: 'None',
      description: 'None',
      key: 'none',
      id: 'none',
    }]
    if (loadingTsg) {
      setCieList([])
    }
    else {
      const response: any = []
      await getParentCIEInstance(tsg, response)
      const res = cieInstance ? [{
        value: `${cieInstance.tenant_instance_name}`,
        description: 'existing instance',
        key: cieInstance.tenant_id,
        id: cieInstance.tenant_id,
      }] : []
      if (!cieInstance) {
        // no CIE instance in selected TSG, create new
        if (enableCieSharing) {
          isNonemptyArray(response) && res.push(...response)
          res.push(...dropDownOption)
        }
      }
      setCieList(res)
    }
  }, [cieInstance, enableCieSharing, getParentCIEInstance, loadingTsg, tsg])

  useEffect(() => {
    if (isNonemptyArray(cieList) && cieInstance) {
      setValue(fieldName, {
        value: cieInstance.tenant_instance_name,
        description: cieInstance.tenant_instance_name,
        key: cieInstance.tenant_id,
        id: cieInstance.tenant_id,
      })
      setCieDropdownDisable(true)
      setCIETitle(cieInstance.tenant_instance_name)
    }
    else {
      setCieDropdownDisable(false)
      resetField(fieldName)
      setCIETitle('')
    }

  }, [cieInstance, cieList, getCieList, resetField, setValue])

  useEffect(() => {
    getCieList()
  }, [getCieList])

  useEffect(() => {
    if (typeof updateTitle === 'function') {
      updateTitle(`${TITLE}: ${cieTitle}`)
    }
  }, [cieTitle, updateTitle])

  useEffect(() => {
    if (typeof setDisable !== 'function') {
      return
    }
    if (state.supportAccountId && (state.tsg || state.tsgs[0].children?.length === 0)) {
      return setDisable(false)
    }
    setDisable(true)
  }, [setDisable, state.tsg, state.tsgs, state.supportAccountId])

  useEffect(() => {
    if (isCieDropdownDisabled) {
      const cieFormValue = getValues(fieldName)
      trigger(fieldName)
      if (cieFormValue.id !== 'none') {
        updateActivationInstances({
          [APP_ID_DSS_ID]: {
            app_id: APP_ID_DSS_ID,
            activation_type: cieFormValue.id === 'new' ? 'NEW' : 'AMEND',
            allocation: [{
              app_id: APP_ID_DSS_ID,
              tenant_id: cieFormValue.id
            }]
          }
        })
      }
      else {
        updateActivationInstances({
          [APP_ID_DSS_ID]: undefined
        })
      }
    }
  }, [getValues, isCieDropdownDisabled, trigger])
  const changeCIEHandler = useCallback((e) => {
    setCIETitle(e.value)
    if (e.id !== 'none') {
      updateActivationInstances({
        [APP_ID_DSS_ID]: {
          app_id: APP_ID_DSS_ID,
          activation_type: e.id === 'new' ? 'NEW' : 'AMEND',
          allocation: [{
            app_id: APP_ID_DSS_ID,
            tenant_id: e.id
          }]
        }
      })
    }
    else {
      updateActivationInstances({
        [APP_ID_DSS_ID]: undefined
      })
    }
  }, [])
  const cieValidator = useCallback((value) => {
    if ((!value || value.id === '') && !isCieDropdownDisabled) {
      setError('cie', { message: props?.cieDropDownProps?.display?.errorMessage })
      if (typeof setBanner === 'function') {
        setBanner({ display: true, message: 'CIE selection error', appearance: 'error' })
      }
      return false
    }
    if (typeof setBanner === 'function') {
      setBanner({ display: false })
    }
    clearErrors('cie')
    return true

  }, [clearErrors, isCieDropdownDisabled, props?.cieDropDownProps?.display?.errorMessage, setBanner, setError])

  const displayDetails = useMemo(() => {
    const dropdown = <DropDown
      {...{
        ...props.cieDropDownProps,
        dropDownList: cieList,
        isLoading: loadingTsg,
        fieldName,
        disabled: isCieDropdownDisabled,
        callbacks: {
          changeHandlerCallback: changeCIEHandler,
          customValidationCallback: cieValidator,
          //setBanner
        }
      }} />
    const details = {
      title: cieManifest?.display_name || 'Cloud Identity Engine',
      data: [
        {
          label: 'CIE dropdown',
          value: dropdown,
          hideLabel: true
        }
      ],
      showBottomDivider: false
    }
    return details
  }, [props.cieDropDownProps, cieList, loadingTsg, isCieDropdownDisabled, changeCIEHandler, cieValidator, cieManifest?.display_name])

  return (
    <KeyValueDisplay {...displayDetails} />
  )
}

export default DefaultCIEField
